import React from "react";
import { ArticleLayout, H2, H3, Quote, Table } from "./ArticleLayout";

const COMPARISON_HEADERS = ["Critério", "Michelin Primacy 5", "Hankook Ventus Prime 4", "Vencedor"];
const COMPARISON_ROWS = [
  ["Travagem em molhado (80 km/h)", "~28 m", "~29 m", "Michelin (ligeiro)"],
  ["Travagem em seco (80 km/h)", "~27 m", "~26 m", "Hankook"],
  ["Aderência em curva", "Excelente", "Excelente", "Empate"],
  ["Aquaplaning", "Top da classe", "Muito bom", "Michelin (ligeiro)"],
  ["Ruído de rolagem", "~68 dB", "~68 dB", "Empate"],
  ["Durabilidade estimada", "55.000–60.000 km", "50.000–55.000 km", "Michelin (ligeiro)"],
  ["Consumo de combustível", "Classe B", "Classe B", "Empate"],
  ["Sensação de direção", "Suave e linear", "Incisiva e desportiva", "Preferência pessoal"],
  ["Preço médio (205/55 R16)", "120–135 €", "85–105 €", "Hankook (–25%)"],
];

export const HankookVsMichelin = () => {
  return (
    <ArticleLayout
      testId="article-hankook-vs-michelin"
      tag="Comparativo · 2026"
      title="Hankook Ventus Prime 4 vs Michelin Primacy 5: qual escolher?"
      readTime="8 min"
      image="/tires/hankook-ventus-prime4.png"
      intro="Dois dos pneus de turismo mais procurados em medidas como 205/55 R16 ou 225/45 R17. Ambos premium, ambos com excelentes avaliações — mas com filosofias diferentes. Comparámos os dois com base em testes independentes (ADAC, Tyre Reviews, Al Volante) e na nossa experiência de oficina."
    >
      <H2>O que são estes pneus</H2>
      <p>
        O <strong>Michelin Primacy 5</strong> é a 5ª geração da linha
        Primacy, lançada em 2024. É um pneu de turismo premium concebido
        para ser o mais seguro do segmento em piso molhado e o mais
        duradouro no seu escalão.
      </p>
      <p>
        O <strong>Hankook Ventus Prime 4 (K135)</strong> é o competidor
        direto sul-coreano e um dos pneus mais reconhecidos da nova geração.
        A Hankook é fornecedor de origem da <strong>Audi, BMW, Mercedes,
        Porsche e VW</strong> — credibilidade técnica que poucos rivais
        conseguem igualar. Em 2026, o Prime 4 manteve-se no top 5 do teste
        de pneus de verão da ADAC, com a melhor nota da sua categoria de
        preço.
      </p>

      <H2>Confronto direto</H2>
      <Table
        headers={COMPARISON_HEADERS}
        rows={COMPARISON_ROWS}
      />

      <Quote>
        O Michelin lidera por margens curtas em segurança e longevidade. O
        Hankook entrega 90% do desempenho por 75% do preço — e ganha em
        seco. Ambos são premium; a melhor escolha depende do seu perfil.
      </Quote>

      <H2>Qual trava melhor na chuva?</H2>
      <p>
        Em testes recentes (ADAC 2026, Al Volante 2025, AutoBild,
        TyreReviews), o Michelin Primacy 5 trava cerca de{" "}
        <strong>1 metro mais curto</strong> em piso molhado a 80 km/h. É
        uma vantagem real mas curta — o Hankook continua entre os melhores
        da categoria e a diferença só se torna decisiva em aquaplaning ou
        em piso muito ensaboado.
      </p>
      <p>
        Para condução em Portugal — invernos chuvosos e estradas com piso
        polido — o Michelin tem vantagem técnica. Ainda assim, qualquer
        dos dois supera com folga 90% dos pneus mid-range no mercado.
      </p>

      <H2>Qual dura mais quilómetros?</H2>
      <p>
        O Michelin Primacy 5 utiliza um composto "Energy" otimizado para
        baixa abrasão e tem fama de longevidade. Em utilização real, a
        diferença para o Hankook é menor do que muitos esperam, sobretudo
        depois das melhorias introduzidas no Ventus Prime 4 face à geração
        anterior. Em condução normal:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Michelin Primacy 5</strong>: 55.000 a 60.000 km típicos</li>
        <li><strong>Hankook Ventus Prime 4</strong>: 50.000 a 55.000 km típicos</li>
      </ul>
      <p>
        Diferença real: cerca de <strong>5.000 km a favor do Michelin</strong>,
        consoante a condução. Em condução mais agressiva ou em frota, a
        diferença pode estreitar ainda mais.
      </p>

      <H2>E quem vence em seco?</H2>
      <p>
        Aqui o Hankook leva vantagem clara. Composto mais agressivo e
        blocos de piso mais densos traduzem-se em <strong>travagem em seco
        mais curta</strong> (~1 m a 100 km/h) e numa sensação de direção
        mais incisiva e desportiva em curva. Não é por acaso que a Hankook
        equipa de origem modelos como o BMW Série 3, Audi A4 ou Porsche
        Macan: a marca dominou os testes de circuito nos últimos anos.
        Para quem privilegia condução desportiva, para quem vive em zonas
        predominantemente secas, ou para quem simplesmente gosta de uma
        direção mais "afiada" — o Hankook é a escolha mais entusiasta.
      </p>

      <H2>O fator preço — onde o Hankook brilha</H2>
      <p>
        Em medidas comuns (205/55 R16, 225/45 R17), o Hankook custa em
        média <strong>25 a 30% menos</strong> que o Michelin. Para uma
        montagem de 4 pneus em 205/55 R16:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Michelin Primacy 5</strong>: ~480–540 € (4 pneus)</li>
        <li><strong>Hankook Ventus Prime 4</strong>: ~340–420 € (4 pneus)</li>
      </ul>
      <p>
        Poupança imediata: <strong>~120–140 €</strong> a favor do Hankook.
        Quando se calcula o <em>custo por quilómetro</em>, o Hankook
        praticamente <strong>vence</strong> — entrega quase a mesma
        quilometragem por bastante menos dinheiro. É por isso que é o pneu
        favorito de muitas empresas de aluguer e gestoras de frotas.
      </p>

      <H2>Veredito</H2>
      <p>
        Os dois pneus jogam na mesma divisão. O Michelin termina à frente
        por uma margem curta em segurança e durabilidade; o Hankook
        responde com travagem em seco superior, melhor preço e
        credenciais de fornecedor de origem dos maiores fabricantes
        europeus.
      </p>

      <H3>Escolha Michelin Primacy 5 se...</H3>
      <ul className="list-disc pl-6 space-y-2">
        <li>Conduz muito em piso molhado ou em zonas com chuva frequente</li>
        <li>Quer o máximo absoluto em durabilidade</li>
        <li>Privilegia o conforto suave e linear sobre tudo o resto</li>
        <li>Não está limitado ao orçamento inicial</li>
      </ul>

      <H3>Escolha Hankook Ventus Prime 4 se...</H3>
      <ul className="list-disc pl-6 space-y-2">
        <li>Quer um pneu premium ao melhor preço possível</li>
        <li>Prefere uma direção incisiva e travagem em seco mais curta</li>
        <li>Conduz um BMW, Audi ou Mercedes — manter o pneu de origem é vantagem</li>
        <li>Vê o custo por quilómetro como o critério decisivo</li>
        <li>Faz menos de 25.000 km por ano</li>
      </ul>

      <p>
        Na Global Pneus temos ambos em stock nas medidas mais comuns.
        Diga-nos as suas condições de utilização e ajudamo-lo a decidir.
      </p>
    </ArticleLayout>
  );
};

export default HankookVsMichelin;
